import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { 
  AlertTriangle, Clock, Check, X, ArrowLeft, 
  ChevronLeft, ChevronRight, Flag, Wifi, Award, Zap
} from "lucide-react";
import { useExamMonitor } from "../hooks/useExamPolling";

const ExamPage = () => {
  const { examId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [exam, setExam] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [attemptId, setAttemptId] = useState(null);
  const [startedAt, setStartedAt] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null);
  const [showAchievementPopup, setShowAchievementPopup] = useState(false);
  const [completedMissions, setCompletedMissions] = useState([]);
  const [earnedBadges, setEarnedBadges] = useState([]);
  const timerRef = useRef(null);
  const serverTimeOffsetRef = useRef(0);
  
  // Scroll state for gradient indicator
  const [canScrollUp, setCanScrollUp] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(false);
  const contentRef = useRef(null);
  
  // Real-time exam state monitoring
  const { exam: polledExam, timeRemaining: serverTimeRemaining, serverTimestamp } = useExamMonitor(examId, !results);
  
  // Check scroll position to show/hide gradient indicator
  useEffect(() => {
    const checkScroll = () => {
      if (contentRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
        setCanScrollUp(scrollTop > 20);
        setCanScrollDown(scrollTop < scrollHeight - clientHeight - 20);
      }
    };
    
    const contentEl = contentRef.current;
    if (contentEl) {
      contentEl.addEventListener('scroll', checkScroll);
      checkScroll();
      setTimeout(checkScroll, 100);
      window.addEventListener('resize', checkScroll);
      
      return () => {
        contentEl.removeEventListener('scroll', checkScroll);
        window.removeEventListener('resize', checkScroll);
      };
    }
  }, [currentIndex, questions]);
  
  // Sync server time offset
  useEffect(() => {
    if (serverTimestamp) {
      serverTimeOffsetRef.current = Date.now() - serverTimestamp;
    }
  }, [serverTimestamp]);
  
  // Sync time from server polling
  useEffect(() => {
    if (serverTimeRemaining !== null && serverTimeRemaining !== undefined && !results) {
      const correctedTime = serverTimeRemaining * 1000;
      if (Math.abs(correctedTime - (timeLeft || 0)) > 3000) {
        setTimeLeft(correctedTime);
      }
    }
  }, [serverTimeRemaining, results]);
  
  // Use refs to access current state in timer callback
  const answersRef = useRef(answers);
  const questionsRef = useRef(questions);
  const submittingRef = useRef(submitting);
  const submitExamRef = useRef(null);
  
  // Keep refs in sync with state
  useEffect(() => { answersRef.current = answers; }, [answers]);
  useEffect(() => { questionsRef.current = questions; }, [questions]);
  useEffect(() => { submittingRef.current = submitting; }, [submitting]);

  const startExam = async () => {
    try {
      const response = await axios.post(
        `${API}/exams/${examId}/start`,
        {},
        { withCredentials: true }
      );
      
      setExam(response.data.exam);
      setQuestions(response.data.questions);
      setAttemptId(response.data.attempt_id);
      setStartedAt(response.data.started_at);
      
      const restoredAnswers = response.data.answers || {};
      setAnswers(restoredAnswers);
      
      if (response.data.server_time_remaining !== null && response.data.server_time_remaining !== undefined) {
        setTimeLeft(response.data.server_time_remaining * 1000);
      }
    } catch (error) {
      console.error("Error starting exam:", error);
      toast.error(error.response?.data?.detail || "Erro ao iniciar exame");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  const submitExam = useCallback(async (isTimeout = false) => {
    if (submittingRef.current) return;
    setSubmitting(true);
    submittingRef.current = true;
    
    const currentAnswers = answersRef.current;
    const currentQuestions = questionsRef.current;
    
    const answersWithIds = {};
    for (const [questionId, selectedIndex] of Object.entries(currentAnswers)) {
      const question = currentQuestions.find(q => q.question_id === questionId);
      if (question && question.answers && question.answers[selectedIndex]) {
        const selectedAnswer = question.answers[selectedIndex];
        answersWithIds[questionId] = selectedAnswer?.id || selectedAnswer;
      }
    }
    
    try {
      const response = await axios.post(
        `${API}/exams/${examId}/submit`,
        { answers: answersWithIds },
        { withCredentials: true }
      );
      
      setResults(response.data);
      if (timerRef.current) clearInterval(timerRef.current);
      
      if (isTimeout) {
        toast.info("Tempo esgotado! O exame foi submetido automaticamente.");
      }
    } catch (error) {
      console.error("Error submitting exam:", error);
      toast.error(error.response?.data?.detail || "Erro ao submeter exame");
    } finally {
      setSubmitting(false);
      submittingRef.current = false;
    }
  }, [examId]);

  useEffect(() => { submitExamRef.current = submitExam; }, [submitExam]);

  // CRITICAL: Force submit if instructor stops the exam
  useEffect(() => {
    if (polledExam && !results && !submitting) {
      if (polledExam.status === 'completed' || polledExam.awaiting_submission) {
        toast.warning('⏱️ O instrutor terminou o exame!', {
          description: 'A submeter as suas respostas...',
          duration: 5000
        });
        
        if (submitExamRef.current) {
          submitExamRef.current(true);
        }
      }
    }
  }, [polledExam, results, submitting]);

  useEffect(() => {
    startExam();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [examId]);

  useEffect(() => {
    if (timeLeft !== null && timeLeft > 0 && !results) {
      timerRef.current = setInterval(() => {
        setTimeLeft(prev => {
          const remaining = Math.max(0, prev - 1000);
          if (remaining <= 0) {
            clearInterval(timerRef.current);
            if (submitExamRef.current) {
              submitExamRef.current(true);
            }
          }
          return remaining;
        });
      }, 1000);
      
      return () => clearInterval(timerRef.current);
    } else if (startedAt && exam && !results && timeLeft === null) {
      const endTime = new Date(startedAt).getTime() + (exam.duration_minutes * 60 * 1000);
      
      timerRef.current = setInterval(() => {
        const now = Date.now();
        const remaining = Math.max(0, endTime - now);
        setTimeLeft(remaining);
        
        if (remaining <= 0) {
          clearInterval(timerRef.current);
          if (submitExamRef.current) {
            submitExamRef.current(true);
          }
        }
      }, 1000);
      
      return () => clearInterval(timerRef.current);
    }
  }, [timeLeft, startedAt, exam, results]);

  const selectAnswer = async (questionId, answerIndex) => {
    const question = questions.find(q => q.question_id === questionId);
    const selectedAnswer = question?.answers[answerIndex];
    const answerId = selectedAnswer?.id || selectedAnswer;
    
    setAnswers(prev => ({
      ...prev,
      [questionId]: answerIndex
    }));
    
    try {
      await axios.post(
        `${API}/exams/${examId}/save-answer`,
        { question_id: questionId, answer_id: answerId },
        { withCredentials: true }
      );
    } catch (error) {
      console.error(`Failed to save answer:`, error);
    }
  };

  const formatTime = (ms) => {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cockpit-bg flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-400 font-mono">A carregar exame...</p>
        </div>
      </div>
    );
  }

  // Results Screen
  if (results) {
    return (
      <div className="min-h-screen bg-cockpit-bg flex items-center justify-center p-6">
        <Card className="instrument-panel max-w-2xl w-full">
          <CardContent className="p-8">
            <div className="text-center mb-8">
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 bg-orange-500/20">
                <Clock className="w-10 h-10 text-orange-400" />
              </div>

              <h2 className="font-chivo text-2xl text-cockpit-text-bright mb-4">
                Exame Submetido!
              </h2>
              
              <div className="p-4 bg-slate-900/50 rounded-lg border border-slate-700 mb-6">
                <p className="text-slate-300 mb-2">
                  O seu exame foi submetido com sucesso.
                </p>
                <p className="text-slate-400 text-sm">
                  A aguardar correção pelo instrutor. Assim que estiver corrigido, poderá consultar o resultado e rever as suas respostas.
                </p>
              </div>
              
              <div className="text-xs text-slate-500 font-mono">
                Submetido em: {new Date(results.submitted_at).toLocaleString('pt-PT')}
              </div>
            </div>

            <Button 
              onClick={() => navigate("/dashboard")}
              data-testid="back-dashboard-btn"
              className="btn-cockpit w-full"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar ao Painel
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const currentQuestion = questions[currentIndex];
  const isUrgent = timeLeft && timeLeft < 5 * 60 * 1000;

  return (
    <div className="h-screen bg-cockpit-bg flex flex-col overflow-hidden">
      {/* Header - Fixed at top */}
      <header className={`border-b bg-cockpit-panel/50 backdrop-blur-sm z-50 shrink-0 ${
        isUrgent ? 'border-red-500' : 'border-cockpit-border'
      }`}>
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-4">
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => {
                  if (confirm("Deseja sair do exame? As suas respostas estão guardadas e pode continuar mais tarde.")) {
                    navigate("/dashboard");
                  }
                }}
                className="text-slate-400 hover:text-white"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="font-chivo text-base sm:text-lg text-cockpit-text-bright line-clamp-1">{exam?.title}</h1>
                <p className="text-xs text-slate-500 font-mono">
                  Questão {currentIndex + 1} de {questions.length}
                </p>
              </div>
            </div>

            {/* Timer */}
            <div className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg ${
              isUrgent 
                ? 'bg-red-500/20 border border-red-500/50 animate-pulse' 
                : 'bg-slate-800'
            }`}>
              <div className="hidden sm:flex items-center gap-1">
                <Wifi className="w-3 h-3 text-emerald-400" title="Sincronizado com servidor" />
              </div>
              <Clock className={`w-4 sm:w-5 h-4 sm:h-5 ${isUrgent ? 'text-red-400' : 'text-orange-400'}`} />
              <span className={`font-mono text-base sm:text-lg ${isUrgent ? 'text-red-400' : 'text-orange-400'}`}>
                {timeLeft ? formatTime(timeLeft) : '--:--'}
              </span>
            </div>
          </div>
          
          {/* Progress bar */}
          <div className="mt-3 sm:mt-4 flex gap-0.5 sm:gap-1">
            {questions.map((q, i) => (
              <button
                key={i}
                onClick={() => setCurrentIndex(i)}
                className={`flex-1 h-1.5 sm:h-2 rounded-full transition-all ${
                  answers[q.question_id] !== undefined
                    ? 'bg-emerald-500'
                    : i === currentIndex
                    ? 'bg-orange-500'
                    : 'bg-slate-700'
                }`}
              />
            ))}
          </div>
        </div>
      </header>

      {/* Main Content - Scrollable area with fixed height */}
      <main className="flex-1 relative overflow-hidden">
        {/* Top scroll gradient indicator */}
        <div className={`absolute top-0 left-0 right-0 h-16 z-10 pointer-events-none transition-opacity duration-300 ${
          canScrollUp ? 'opacity-100' : 'opacity-0'
        }`} style={{ background: 'linear-gradient(to bottom, rgb(15, 23, 42) 0%, rgb(15, 23, 42, 0.8) 40%, transparent 100%)' }} />
        
        {/* Bottom scroll gradient indicator */}
        <div className={`absolute bottom-0 left-0 right-0 h-32 z-10 pointer-events-none transition-opacity duration-300 ${
          canScrollDown ? 'opacity-100' : 'opacity-0'
        }`} style={{ background: 'linear-gradient(to top, rgb(15, 23, 42) 0%, rgb(15, 23, 42, 0.9) 30%, rgb(15, 23, 42, 0.5) 60%, transparent 100%)' }} />
        
        {/* Scrollable content */}
        <div 
          ref={contentRef}
          className="h-full overflow-y-auto px-4 sm:px-6 py-6 pb-48 sm:pb-40"
        >
          <div className="container mx-auto max-w-4xl">
            {currentQuestion && (
              <>
                {/* Section Badge */}
                <div className="badge-cockpit text-orange-400 inline-block mb-4">
                  {currentQuestion.section_name}
                </div>

                {/* Question Statement */}
                <Card className="instrument-panel mb-6">
                  <CardContent className="p-4 sm:p-6">
                    <p className="text-cockpit-text-bright text-base sm:text-lg leading-relaxed">
                      {currentQuestion.statement}
                    </p>
                    {currentQuestion.image && (
                      <img 
                        src={currentQuestion.image} 
                        alt="Question" 
                        className="mt-4 rounded-lg max-h-64 object-contain"
                      />
                    )}
                  </CardContent>
                </Card>

                {/* Answer Options */}
                <div className="space-y-3">
                  {currentQuestion.answers.map((answer, displayIndex) => {
                    const answerText = answer?.text || answer;
                    
                    return (
                      <ExamAnswerOption
                        key={displayIndex}
                        index={displayIndex}
                        answer={answerText}
                        selected={answers[currentQuestion.question_id] === displayIndex}
                        onClick={() => selectAnswer(currentQuestion.question_id, displayIndex)}
                      />
                    );
                  })}
                </div>

                {/* Quick Navigation - Inline with content */}
                <div className="mt-6 flex flex-wrap gap-2 justify-center">
                  {questions.length <= 15 ? (
                    questions.map((q, i) => (
                      <button
                        key={i}
                        onClick={() => setCurrentIndex(i)}
                        className={`w-8 h-8 rounded text-xs font-mono transition-all ${
                          answers[q.question_id] !== undefined
                            ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/50'
                            : i === currentIndex
                            ? 'bg-orange-500 text-white'
                            : 'bg-slate-800 text-slate-400 border border-slate-700'
                        }`}
                      >
                        {i + 1}
                      </button>
                    ))
                  ) : (
                    <>
                      {[0, 1].map(i => questions[i] && (
                        <button
                          key={i}
                          onClick={() => setCurrentIndex(i)}
                          className={`w-8 h-8 rounded text-xs font-mono transition-all ${
                            answers[questions[i].question_id] !== undefined
                              ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/50'
                              : i === currentIndex
                              ? 'bg-orange-500 text-white'
                              : 'bg-slate-800 text-slate-400 border border-slate-700'
                          }`}
                        >
                          {i + 1}
                        </button>
                      ))}
                      
                      {currentIndex > 3 && <span className="text-slate-500 px-1">···</span>}
                      
                      {Array.from({ length: 3 }, (_, idx) => currentIndex - 1 + idx)
                        .filter(i => i > 1 && i < questions.length - 2)
                        .map(i => questions[i] && (
                          <button
                            key={i}
                            onClick={() => setCurrentIndex(i)}
                            className={`w-8 h-8 rounded text-xs font-mono transition-all ${
                              answers[questions[i].question_id] !== undefined
                                ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/50'
                                : i === currentIndex
                                ? 'bg-orange-500 text-white'
                                : 'bg-slate-800 text-slate-400 border border-slate-700'
                            }`}
                          >
                            {i + 1}
                          </button>
                        ))
                      }
                      
                      {currentIndex < questions.length - 4 && <span className="text-slate-500 px-1">···</span>}
                      
                      {[questions.length - 2, questions.length - 1].map(i => questions[i] && (
                        <button
                          key={i}
                          onClick={() => setCurrentIndex(i)}
                          className={`w-8 h-8 rounded text-xs font-mono transition-all ${
                            answers[questions[i].question_id] !== undefined
                              ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/50'
                              : i === currentIndex
                              ? 'bg-orange-500 text-white'
                              : 'bg-slate-800 text-slate-400 border border-slate-700'
                          }`}
                        >
                          {i + 1}
                        </button>
                      ))}
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </main>

      {/* Fixed Navigation Bar at Bottom */}
      <div className="fixed bottom-0 left-0 right-0 z-50">
        {/* Gradient backdrop for visual separation */}
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(to top, rgb(15, 23, 42) 0%, transparent 100%)' }} />
        
        {/* Navigation content with blur */}
        <div className="relative bg-slate-900/95 backdrop-blur-xl border-t border-slate-700/50 shadow-2xl shadow-black/50">
          <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4">
            {/* Mobile: Stacked layout */}
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
              {/* Navigation buttons row */}
              <div className="flex gap-2 sm:gap-4 w-full">
                <Button 
                  variant="outline"
                  onClick={() => setCurrentIndex(Math.max(0, currentIndex - 1))}
                  disabled={currentIndex === 0}
                  className="btn-secondary-cockpit flex-1 py-3 sm:py-2"
                >
                  <ChevronLeft className="w-5 h-5 sm:mr-2" />
                  <span className="hidden sm:inline">Anterior</span>
                </Button>
                
                {currentIndex < questions.length - 1 ? (
                  <Button 
                    onClick={() => setCurrentIndex(currentIndex + 1)}
                    className="btn-cockpit flex-1 py-3 sm:py-2"
                  >
                    <span className="hidden sm:inline">Próxima</span>
                    <ChevronRight className="w-5 h-5 sm:ml-2" />
                  </Button>
                ) : (
                  <Button 
                    onClick={() => {
                      const unanswered = questions.filter(q => answers[q.question_id] === undefined).length;
                      const message = unanswered > 0 
                        ? `Tem ${unanswered} questões por responder. Deseja submeter o exame? Esta ação não pode ser revertida.`
                        : `Tem a certeza que deseja submeter o exame? Esta ação não pode ser revertida.`;
                      if (!confirm(message)) {
                        return;
                      }
                      submitExam(false);
                    }}
                    disabled={submitting}
                    data-testid="submit-exam-btn"
                    className="btn-cockpit flex-1 py-3 sm:py-2 bg-emerald-600 hover:bg-emerald-500"
                  >
                    {submitting ? (
                      <span className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span className="hidden sm:inline">A submeter...</span>
                      </span>
                    ) : (
                      <>
                        <Check className="w-5 h-5 sm:mr-2" />
                        <span className="hidden sm:inline">Submeter Exame</span>
                        <span className="sm:hidden">Submeter</span>
                      </>
                    )}
                  </Button>
                )}
              </div>
              
              {/* Progress indicator for mobile */}
              <div className="flex sm:hidden items-center justify-center text-xs text-slate-500 font-mono">
                <span className="text-emerald-400">{Object.keys(answers).length}</span>
                <span className="mx-1">/</span>
                <span>{questions.length} respondidas</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Achievement Popup */}
      <Dialog open={showAchievementPopup} onOpenChange={setShowAchievementPopup}>
        <DialogContent className="bg-cockpit-panel border-cockpit-border max-w-md">
          <DialogHeader>
            <DialogTitle className="font-chivo text-center flex items-center justify-center gap-2">
              <Award className="w-6 h-6 text-yellow-400" />
              {earnedBadges.length > 0 && completedMissions.length > 0 
                ? 'Conquistas Desbloqueadas!' 
                : earnedBadges.length > 0 
                  ? 'Novo Badge!' 
                  : 'Missão Completa!'}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {completedMissions.length > 0 && (
              <div className="space-y-2">
                {completedMissions.map((mission, idx) => (
                  <div key={idx} className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 text-center">
                    <div className="flex items-center justify-center gap-2 mb-1">
                      <Zap className="w-5 h-5 text-yellow-400" />
                      <span className="text-cockpit-text-bright font-medium">{mission.title || 'Missão'}</span>
                    </div>
                    {mission.description && (
                      <p className="text-xs text-slate-400">{mission.description}</p>
                    )}
                    <div className="text-xs text-yellow-400 mt-1">+{mission.xp_reward || 0} XP</div>
                  </div>
                ))}
              </div>
            )}
            
            {earnedBadges.length > 0 && (
              <div className="space-y-2">
                {earnedBadges.map((badge, idx) => (
                  <div key={idx} className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3 text-center">
                    <div className="text-3xl mb-2">{badge.icon || '🏆'}</div>
                    <div className="text-cockpit-text-bright font-medium">{badge.name || 'Badge'}</div>
                    {badge.description && (
                      <p className="text-xs text-slate-400 mt-1">{badge.description}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
            
            <Button 
              onClick={() => setShowAchievementPopup(false)}
              className="btn-cockpit w-full"
            >
              Continuar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const ExamAnswerOption = ({ index, answer, selected, onClick }) => {
  const letters = ['A', 'B', 'C', 'D'];

  return (
    <button
      onClick={onClick}
      data-testid={`exam-answer-${index}`}
      className={`w-full p-4 rounded-lg border transition-all text-left flex items-start gap-4 ${
        selected 
          ? 'border-orange-500 bg-orange-500/10' 
          : 'border-slate-700 hover:border-slate-600 bg-slate-900/50'
      }`}
    >
      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-mono shrink-0 ${
        selected ? 'bg-orange-500 text-white' : 'bg-slate-800 text-slate-400'
      }`}>
        {letters[index]}
      </div>
      <span className="text-cockpit-text-bright">{answer}</span>
    </button>
  );
};

export default ExamPage;
